import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Loader2, Save, FileDown,
  CheckCircle2, ShieldCheck, HardDrive, MessageSquare,
  UserX, LayoutTemplate, CheckSquare, FileText, X, ThumbsUp
} from 'lucide-react';
import { offboardingService } from '../../services/offboardingService';
import { useAuth } from '../../context/AuthContext';
import StatusBadge from '../../components/StatusBadge';

const ordinalDay = (n) => {
  const v = n % 100;
  const suffix = ['th', 'st', 'nd', 'rd'];
  return n + (suffix[(v - 20) % 10] || suffix[v] || suffix[0]);
};

const formatLetterDate = (value) => {
  const raw = String(value || '').slice(0, 10);
  const [y, m, d] = raw.split('-').map(Number);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  if (!y || !m || !d) return raw;
  return `${ordinalDay(d)} ${months[m - 1]}, ${y}`;
};

const describeEmploymentType = (type) => {
  const key = String(type || '').toLowerCase().replace(/[\s_-]/g, '');
  if (!key || key === 'fulltime' || key === 'permanent') return 'permanent employee';
  return `${String(type).toLowerCase()} employee`;
};

const describeExitMoment = (reason) => {
  if (reason === 'Resignation') return 'At the time of resignation';
  if (reason === 'End of Contract') return 'At the end of the contract';
  return 'At the time of leaving the organization';
};

const TEMPLATES = {
  standard: "This is to certify that {NAME} was the {TYPE} of Synvex Private Limited from {FROM} to {TO}. {ATTIME}, they were working as {DESIGNATION}{DEPT}.\n\nDuring their tenure, we found them hard working, committed and dedicated towards their job.",
  appreciative: "It is a pleasure to write this experience letter for {NAME}, who was the {TYPE} of Synvex Private Limited from {FROM} to {TO}. {ATTIME}, they were working as {DESIGNATION}{DEPT}.\n\nThey have been an exceptional asset to the team, consistently delivering high-quality work. Their technical skills, problem-solving abilities, and team spirit are highly commendable. We highly recommend them for any future roles.",
  basic: "This document confirms that {NAME} was the {TYPE} of Synvex Private Limited from {FROM} to {TO}, working as {DESIGNATION}{DEPT}.\n\nAll company property has been returned and all departmental clearances have been obtained as of their final working day."
};

const OffboardingDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);
  const [letterBody, setLetterBody] = useState('');

  const isSuperAdmin = user && user.role === 'super_admin';

  const loadRecord = async () => {
    try {
      const res = await offboardingService.getOne(id);
      if (res.data) {
        setRecord(res.data);
        setLetterBody(res.data.experience_letter_draft || '');
      }
    } catch (err) {
      console.error("Error loading record:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadRecord(); }, [id]);

  const toggleChecklist = async (key, val) => {
    try {
      const updateData = {};
      updateData[key] = !val;
      await offboardingService.updateChecklist(id, updateData);
      loadRecord();
    } catch (err) {
      alert(err.message);
    }
  };

  const applyTemplate = (templateKey) => {
    if (!record) return;
    const fill = {
      NAME: record.employee_name,
      TYPE: describeEmploymentType(record.employment_type),
      FROM: formatLetterDate(record.joining_date),
      TO: formatLetterDate(record.last_working_date),
      ATTIME: describeExitMoment(record.reason),
      DESIGNATION: record.designation,
      DEPT: record.department ? ` in the ${record.department} Department` : '',
    };
    const text = TEMPLATES[templateKey].replace(/\{(\w+)\}/g, (match, key) => (fill[key] !== undefined ? fill[key] : match));
    setLetterBody(text);
  };

  const handleSaveAndGenerate = async () => {
    setSaving(true);
    try {
      await offboardingService.updateLetterDraft(id, letterBody);
      await offboardingService.generateLetter(id);
      alert("Experience letter generated successfully!");
      loadRecord();
    } catch (err) {
      alert("Error: " + (err.message || "Backend connection failed"));
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async () => {
    if (!window.confirm("Confirm approval of this offboarding process?")) return;
    setApproving(true);
    try {
      await offboardingService.approveOffboarding(id);
      loadRecord();
    } catch (err) {
      alert("Error: " + (err.message || "Approval failed"));
    } finally {
      setApproving(false);
    }
  };

  const downloadLetter = () => {
    const url = offboardingService.getDownloadUrl(id);
    const token = localStorage.getItem('access_token');
    window.open(url + '?token=' + token, '_blank');
  };

  if (loading) return (
    <div className="flex justify-center p-20">
      <Loader2 className="animate-spin text-primary w-10 h-10 opacity-20" />
    </div>
  );

  if (!record) return (
    <div className="p-10 text-center text-gray-500">
      <X className="w-10 h-10 mx-auto mb-2 opacity-20" />
      Offboarding record not found.
    </div>
  );

  const allChecklistDone = record.checklist &&
    Object.values(record.checklist).every(Boolean);

  return (
    <div className="max-w-6xl mx-auto space-y-6 pb-20">
      <button
        onClick={() => navigate('/offboarding')}
        className="flex items-center gap-2 text-gray-500 hover:text-primary transition-colors text-sm font-bold uppercase tracking-tighter"
      >
        <ArrowLeft className="w-4 h-4" /> Back to Offboarding
      </button>

      <div className="bg-white rounded-card border border-gray-100 shadow-soft p-8 flex justify-between items-start flex-wrap gap-4 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-2 h-full bg-primary" />
        <div>
          <h1 className="text-3xl font-black text-gray-900 tracking-tight">{record.employee_name}</h1>
          <div className="flex items-center gap-3 mt-1">
            <p className="text-gray-500 font-bold uppercase text-xs tracking-widest">{record.designation}</p>
            <span className="text-gray-200">|</span>
            <p className="text-gray-500 font-bold uppercase text-xs tracking-widest">{record.department}</p>
          </div>
          <div className="flex items-center gap-4 mt-3">
            <div className="bg-gray-50 border border-gray-100 rounded-xl px-4 py-2">
              <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Join Date</p>
              <p className="text-sm font-bold text-gray-900 mt-0.5">{record.joining_date}</p>
            </div>
            <div className="bg-gray-50 border border-gray-100 rounded-xl px-4 py-2">
              <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Offboard Date</p>
              <p className="text-sm font-bold text-gray-900 mt-0.5">{record.last_working_date}</p>
            </div>
          </div>
        </div>
        <div className="flex flex-col items-end gap-3">
          <StatusBadge status={String(record.status)} />
          <span className="font-mono text-[10px] text-gray-400 bg-gray-50 px-2 py-1 rounded border uppercase font-bold tracking-widest">
            Record ID: {record.offboarding_id}
          </span>
          {isSuperAdmin && record.status !== 'Approved' && record.status !== 'Cancelled' && (
            <button
              onClick={handleApprove}
              disabled={approving}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-5 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider shadow-lg shadow-green-100 transition-all disabled:opacity-60"
            >
              {approving
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <ThumbsUp className="w-4 h-4" />}
              Approve Offboarding
            </button>
          )}
          {record.status === 'Approved' && (
            <span className="flex items-center gap-1.5 text-green-600 text-xs font-black uppercase tracking-wider">
              <CheckCircle2 className="w-4 h-4" /> Approved
            </span>
          )}
        </div>
      </div>

      {allChecklistDone && record.status !== 'Approved' && isSuperAdmin && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-3 text-amber-800 text-sm font-medium">
          All checklist items are complete. Please approve the offboarding from the button above.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        <div className="lg:col-span-1 space-y-4">
          <div className="bg-white rounded-card border border-gray-100 shadow-soft p-6">
            <h2 className="text-[10px] font-black text-primary uppercase tracking-[0.2em] mb-6 flex items-center gap-2 border-b pb-4">
              <CheckSquare className="w-4 h-4" /> Exit Checklist
            </h2>
            <div className="space-y-3">
              <CheckItem
                icon={<HardDrive />} label="Assets Returned"
                checked={!!record.checklist?.assets_returned}
                onToggle={() => toggleChecklist('assets_returned', record.checklist.assets_returned)}
              />
              <CheckItem
                icon={<MessageSquare />} label="Work Handover"
                checked={!!record.checklist?.knowledge_transfer_completed}
                onToggle={() => toggleChecklist('knowledge_transfer_completed', record.checklist.knowledge_transfer_completed)}
              />
              <CheckItem
                icon={<ShieldCheck />} label="Exit Interview"
                checked={!!record.checklist?.exit_interview_completed}
                onToggle={() => toggleChecklist('exit_interview_completed', record.checklist.exit_interview_completed)}
              />
              <CheckItem
                icon={<UserX />} label="Revoke Access"
                checked={!!record.checklist?.access_revoked}
                onToggle={() => {
                  if (window.confirm("CRITICAL: Deactivate system login for " + record.employee_name + "?"))
                    toggleChecklist('access_revoked', record.checklist.access_revoked);
                }}
              />
            </div>
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="bg-white rounded-card border border-gray-100 shadow-soft overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-50 bg-gray-50/30 flex justify-between items-center">
              <h2 className="text-[10px] font-black text-primary uppercase tracking-[0.2em] flex items-center gap-2">
                <FileText className="w-4 h-4" /> Experience Certificate
              </h2>
              {record.experience_letter_generated && (
                <button
                  onClick={downloadLetter}
                  className="bg-green-600 text-white px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-wider flex items-center gap-2 hover:bg-green-700 shadow-lg shadow-green-100 transition-all active:scale-95"
                >
                  <FileDown className="w-4 h-4" /> Download Official PDF
                </button>
              )}
            </div>

            <div className="p-6 space-y-4">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] font-black text-gray-400 uppercase mr-2 tracking-tighter flex items-center gap-1">
                  <LayoutTemplate className="w-3 h-3" /> Quick Content:
                </span>
                <button onClick={() => applyTemplate('standard')} className="text-[10px] font-bold border border-gray-200 px-3 py-1.5 rounded-lg hover:bg-primary hover:text-white transition-all">Standard</button>
                <button onClick={() => applyTemplate('appreciative')} className="text-[10px] font-bold border border-gray-200 px-3 py-1.5 rounded-lg hover:bg-primary hover:text-white transition-all">Appreciative</button>
                <button onClick={() => applyTemplate('basic')} className="text-[10px] font-bold border border-gray-200 px-3 py-1.5 rounded-lg hover:bg-primary hover:text-white transition-all">Basic</button>
              </div>

              <textarea
                value={letterBody}
                onChange={(e) => setLetterBody(e.target.value)}
                className="w-full h-80 p-6 border border-gray-100 rounded-3xl text-sm font-serif leading-relaxed bg-gray-50 focus:bg-white focus:ring-4 focus:ring-primary/5 transition-all outline-none resize-none shadow-inner"
                placeholder="Certificate content here..."
              />

              <div className="flex justify-end pt-4">
                <button
                  onClick={handleSaveAndGenerate}
                  disabled={saving || !letterBody}
                  className="bg-primary text-white px-10 py-3.5 rounded-2xl text-sm font-black uppercase tracking-widest flex items-center gap-2 shadow-xl shadow-primary/20 hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-50"
                >
                  {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
                  Save & Generate PDF
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const CheckItem = ({ icon, label, checked, onToggle }) => (
  <button
    onClick={onToggle}
    className={'w-full flex items-center justify-between p-4 rounded-2xl border transition-all ' + (checked ? 'bg-green-50 border-green-200 shadow-sm' : 'bg-white border-gray-50 hover:border-primary/20')}
  >
    <div className="flex items-center gap-4">
      <div className={'p-2.5 rounded-xl ' + (checked ? 'bg-green-600 text-white shadow-lg shadow-green-100' : 'bg-gray-100 text-gray-400')}>
        {React.cloneElement(icon, { className: "w-5 h-5" })}
      </div>
      <span className={'text-sm font-bold ' + (checked ? 'text-green-700' : 'text-gray-600')}>{label}</span>
    </div>
    {checked && <CheckCircle2 className="w-5 h-5 text-green-600" />}
  </button>
);

export default OffboardingDetail;